import { Link, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { m } from 'motion/react'
import { Button, Section, Price, VoiceEntryList } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider'
import { MANAGED_WEBSITE, OTHER_MANAGED_SERVICES, DFY_BUNDLES, CONSULTING, oneTimeAddOn } from '@gjl/voice/pricing'

// Pricing is single-sourced from @gjl/voice/pricing. Edit prices there.

const SERVICE_DETAILS: Record<string, { headline: string; points: string[] }> = {
  'website-care': {
    headline: 'One update a month, handled',
    points: [
      'One update request per month: a concert change, a new page, a season refresh',
      '72-hour turnaround',
      'Website software included in the price',
      'The lighter tier, between running it yourself and Unlimited',
    ],
  },
  'managed-website': {
    headline: 'Your website, kept current without you touching it',
    points: [
      'Send an update request any time: concert dates, artist bios, new pages',
      'One active request at a time, 72-hour turnaround',
      'Design, copy edits, and technical fixes all included',
      'Website software included in the price',
    ],
  },
  'managed-email': {
    headline: 'Campaigns planned, written, designed, and sent for you',
    points: [
      'About 2 campaigns per month, plus 1 automation',
      'Season announcements, ticket launches, donor appeals, reminders',
      'List management and deliverability handled',
      'Email software included in the price',
    ],
  },
  'managed-design': {
    headline: 'A designer on call for your season',
    points: [
      'Send design requests as they come up: posters, program covers, social graphics',
      'One active request at a time, 72-hour turnaround',
      'Led by Danielle Dinstman, the designer behind Fugue Lab itself',
      'Brand packages and season campaigns available as one-time projects',
    ],
  },
}

export default function DoneForYouPage() {
  const breadcrumbs = generateBreadcrumbs('/services/done-for-you', breadcrumbLabels)

  useSEO({
    title: 'Done-For-You Digital Services for Music Organizations',
    description:
      'Managed website, email, and design services for music organizations, with Concierge for everything else. We operate it, you stay in charge.',
    url: '/services/done-for-you',
    canonical: '/services/done-for-you',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Done-For-You Digital Services',
      description:
        'Managed website and email marketing services for music organizations, with Concierge engagements covering fundraising and box office, underlying software included.',
      url: '/services/done-for-you',
    }),
  })

  // SPA navigation doesn't trigger native anchor scrolling (e.g. /services/done-for-you#managed-email),
  // including hash-only changes while already on this page.
  const location = useLocation()
  useEffect(() => {
    const id = location.hash.slice(1)
    if (!id) return
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }, [location])

  // One entrance, on the hero only. The per-section staggered reveals and
  // card hover lifts were the stock kit and are gone.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    // Sections own their own vertical rhythm now, so the wrapper only sets the
    // measure and the top offset under the fixed header.
    <div className="mx-auto max-w-6xl px-4 pt-16">
      {/* Hero. Deliberately short: this used to carry 55 words across two
          paragraphs, and the second ("include the underlying software, so you
          see one number...") is a term of the engagement, not a hook — it now
          lives in "How engagements work" below, where someone is actually
          looking for it. */}
      <section ref={heroRef} className="mx-auto max-w-3xl space-y-6 pb-20 text-center md:pb-28">
        <h1 className="text-fluid-h1">Done for you, not out of your hands</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-2xl">
          Your website, email, and design work, run by our team instead of yours. You stay in charge.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-2 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/pricing">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              See pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Proof, before the pitch. A done-for-you page's hardest job is showing
          what "we run it" actually produces, and the strongest available answer
          is a real engagement rather than a mood image.

          Holy Trinity Covina is used deliberately. The two other before/after
          pairs in the repo (Brass City Jazz, Coastal Chamber) are labelled
          "(Demo Preview)" in data/portfolio.ts — unsolicited rebuilds from the
          orgs' own public content, which their case-study pages are careful to
          say outright ("it isn't a client engagement, it's a working demo").
          Those are fine on a blog post and wrong here: on a page selling a
          service, a speculative concept presented as delivered work is a false
          claim. Holy Trinity is a real client. */}
      <Section heading="What it looks like when we run it" centered>
        <figure className="mx-auto max-w-4xl">
          <BeforeAfterSlider
            beforeSrc="/images/blog/holy-trinity/before-home.webp"
            afterSrc="/images/blog/holy-trinity/after-home.webp"
            beforeAlt="The old Holy Trinity Covina homepage on WordPress: the stone church building above a dated, cluttered navigation menu"
            afterAlt="The rebuilt Holy Trinity Covina homepage: a clean navigation bar over the congregation gathered outside the church, with a headline and an Upcoming Events button"
            beforeLabel="WordPress"
            afterLabel="Rebuilt"
          />
          <figcaption className="text-muted-foreground mt-4 text-center text-sm">
            Holy Trinity Episcopal Church, Covina. Drag to compare. We rebuilt the site and still run it, so the parish
            sends a request instead of editing a page.{' '}
            <Link
              to="/blog/holy-trinity-case-study"
              className="text-primary focus-visible:outline-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              Read the case study
            </Link>
          </figcaption>
        </figure>
      </Section>

      {/* Managed services. Set as type on the page ground, not as cards.
          The one exception is Managed Website's two tiers (Care / Unlimited):
          those are genuinely side-by-side comparable plans, so they keep a
          plain card ground, with no border, tint or lift. */}
      <Section heading="Delegate one piece, or all of them">
        <div className="space-y-20 md:space-y-24">
          {/* Managed Website is one service with two tiers (Care / Unlimited).
              Outer id anchors #managed-website; the Care tier keeps its own
              #website-care anchor for existing deep links. */}
          <div id={MANAGED_WEBSITE.id} className="scroll-mt-24 space-y-8">
            <div className="grid gap-4 md:grid-cols-12 md:gap-8">
              <div className="space-y-2 md:col-span-5">
                <h3 className="text-fluid-h3">{MANAGED_WEBSITE.name}</h3>
                <Price size="lg">From ${MANAGED_WEBSITE.tiers[0].priceMonthly}/mo</Price>
              </div>
              <p className="text-muted-foreground text-fluid-body max-w-[60ch] md:col-span-7">
                {MANAGED_WEBSITE.blurb}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {MANAGED_WEBSITE.tiers.map((tier) => {
                const details = SERVICE_DETAILS[tier.id]
                return (
                  <div key={tier.id} id={tier.id} className="bg-card scroll-mt-24 space-y-4 rounded-md p-6 md:p-8">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="font-display text-2xl">{tier.label}</h4>
                      <Price>${tier.priceMonthly}/mo</Price>
                    </div>
                    {details && <p className="text-foreground font-medium">{details.headline}</p>}
                    <VoiceEntryList>
                      {(details?.points ?? [tier.note]).map((point) => (
                        <span key={point}>{point}</span>
                      ))}
                    </VoiceEntryList>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="grid gap-x-12 gap-y-16 md:grid-cols-2">
            {OTHER_MANAGED_SERVICES.map((svc) => {
              const details = SERVICE_DETAILS[svc.id]
              return (
                <div key={svc.id} id={svc.id} className="scroll-mt-24 space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-fluid-h4">{svc.name}</h3>
                    <Price>${svc.priceMonthly}/mo</Price>
                  </div>
                  {details && <p className="text-foreground font-medium">{details.headline}</p>}
                  <VoiceEntryList>
                    {(details?.points ?? [svc.note]).map((point) => (
                      <span key={point}>{point}</span>
                    ))}
                  </VoiceEntryList>
                </div>
              )
            })}
          </div>
        </div>
      </Section>

      {/* Bundles + consulting. The page's one change of ground: a full-bleed
          band, so the page has a pulse. Inside it the offers are a typographic
          list (name and price, then the note), not tinted cards. */}
      {/* The page's one atmospheric image. `cello.jpg` was orphaned on
          /services, the stale agency hub slated for retirement, so the best
          photograph on the site was sitting where almost nobody lands. Warm
          instrument wood on near-black is already the brand palette, so it sits
          under the band rather than fighting it. */}
      <Section tone="raised" bleed backdrop="/cello.jpg" backdropPosition="center 35%" heading="Bundles and consulting">
        <div className="grid gap-x-12 gap-y-12 md:grid-cols-2">
          {[...DFY_BUNDLES, ...CONSULTING].map((bundle) => (
            <div key={bundle.id} id={bundle.id} className="scroll-mt-24 space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h3 className="text-fluid-h4">{bundle.name}</h3>
                <Price>{bundle.price}</Price>
              </div>
              <p className="text-muted-foreground max-w-[52ch] leading-relaxed">{bundle.note}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Terms */}
      <Section heading="How engagements work" centered size="compact" className="mx-auto max-w-3xl">
        <div>
          <VoiceEntryList className="space-y-4">
            {[
              // `$250` was a prose literal here even though this file already
              // imports from the pricing catalogue. Read it from the accessor so a
              // reprice cannot leave this line stale.
              `Every done-for-you plan starts with a one-time onboarding. The ${oneTimeAddOn('launch-build').price} Launch build counts, so getting a new site is also your onboarding.`,
              'Managed website and email include the underlying software, so you see one number.',
              'Plans have a 3-month minimum term. After that, month to month.',
              'Managed Website requests are unlimited: one active at a time, 72-hour turnaround.',
              'Take any capability back in-house whenever you want. The software keeps running; only the service stops.',
              'Leaving entirely? Exporting your content, contacts, and donor data is free, always.',
            ].map((term) => (
              <span key={term}>{term}</span>
            ))}
          </VoiceEntryList>
        </div>
        <div className="pt-10 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </Section>
    </div>
  )
}
